import React, { useEffect, useState, useContext } from "react";
import OwnerLayout from "../../../layout/OwnerLayout";
import { FiPlus, FiMapPin, FiWifi, FiWifiOff } from "react-icons/fi";
import { GoPencil } from "react-icons/go";
import { BsTrash3, BsClock, BsShield } from "react-icons/bs";
import { MdOutlineScreenshotMonitor } from "react-icons/md";
import { BiCar } from "react-icons/bi";
import AddVehicle from "./AddVehicle";
import { getCurrentUser } from "../../../../api/user.api";
import { confirmVehicleDelete, success } from "../../../../utils/notifications";
import { deleteVehicle, getVehiclesByOwner } from "../../../../api/vehicle.api";
import { baseURL } from "../../../../api/api";
import { RotatingLines } from "react-loader-spinner";
import { UserContext } from "../../../../context/UserContext";
import placeholderVehicle from "../../../../assets/images/Register Owner Image.png";
import { Link } from "react-router-dom";

const initialData = {
  image: "",
  brand: "",
  model: "",
  type: "",
  service: "",
  ownerId: "",
  registrationNumber: "",
  location: "",
};

function Vehicles() {
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [editVehiclesData, setEditVehiclesData] = useState(null);
  const [ownerProfileExists, setOwnerProfileExists] = useState(null); // Changed initial state to null
  const [loading, setLoading] = useState(true);
  const { user } = useContext(UserContext);

  const fetchVehicles = () => {
    setLoading(true);
    getCurrentUser()
      .then((res) => {
        if (res?.data?.user?.ownerId) {
          setOwnerProfileExists(true);
          return getVehiclesByOwner(res.data.user.ownerId);
        } else {
          setOwnerProfileExists(false);
          return Promise.resolve({ data: { vehicles: [] } });
        }
      })
      .then((res) => {
        setVehiclesData(res?.data?.vehicles || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setOwnerProfileExists(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const handleDelete = (id) => {
    confirmVehicleDelete().then(async (result) => {
      if (result.isConfirmed) {
        await deleteVehicle(id);
        setVehiclesData(vehiclesData.filter((vehicle) => vehicle._id !== id));
        await success("Success!", "Mobile Location deleted successfully!");
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never connected";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-yellow text-black";
      case "Active":
        return "bg-green-100 text-green-800";
      case "Pending Approval":
        return "bg-red-100 text-red-800";
      case "Online":
        return "bg-green-100 text-green-800";
      case "Offline":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getOnlineStatusIcon = (status) => {
    return status === "Online" ? (
      <FiWifi className="w-4 h-4 text-green-500" />
    ) : (
      <FiWifiOff className="w-4 h-4 text-red-500" />
    );
  };

  const VehicleCard = ({ vehicle }) => (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col">
      {/* Vehicle Image */}
      <div className="relative">
        <img
          className="w-full h-48 object-cover rounded-t-xl"
          src={
            vehicle?.image
              ? `${baseURL}/s3/images/${vehicle.image}`
              : placeholderVehicle
          }
          alt={`${vehicle.brand} ${vehicle.model}`}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = placeholderVehicle;
          }}
        />
        <div className="absolute top-4 left-4">
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
              vehicle.status
            )}`}
          >
            {vehicle.status}
          </span>
        </div>
        <div className="absolute top-4 right-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-full p-2">
            <BiCar className="w-5 h-5 text-yellow" />
          </div>
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="p-6 flex-grow">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {vehicle.brand} {vehicle.model}
            </h3>
            <p className="text-gray-600 font-medium">
              {vehicle.registrationNumber}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Service Type</p>
            <p className="text-sm font-semibold text-yellow">{vehicle.type}</p>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center mb-4">
          <FiMapPin className="w-4 h-4 text-gray-500 mr-2" />
          <span className="text-gray-700">{vehicle.location}</span>
        </div>

        {/* Screen Information */}
        <div className="mb-4">
          <div className="flex items-center mb-3">
            <MdOutlineScreenshotMonitor className="w-5 h-5 text-yellow mr-2" />
            <h4 className="font-semibold text-gray-900">Screen Information</h4>
          </div>

          {vehicle.screens && vehicle.screens.length > 0 ? (
            vehicle.screens.map((screen, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 mb-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Model
                    </p>
                    <p className="font-medium text-gray-900">
                      {screen.model || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Status
                    </p>
                    <div className="flex items-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                          screen.status
                        )}`}
                      >
                        {screen.status || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="break-all">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      IMEI
                    </p>
                    <p className="font-medium text-gray-900 text-sm">
                      {screen.imeiNumber || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Orientation
                    </p>
                    <p className="font-medium text-gray-900 capitalize">
                      {screen.defaultOrientation || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Online Status
                    </p>
                    <div className="flex items-center">
                      {getOnlineStatusIcon(screen.onlineStatus)}
                      <span
                        className={`ml-2 text-sm font-medium ${getStatusColor(
                          screen.onlineStatus
                        )} px-2 py-0.5 rounded-full`}
                      >
                        {screen.onlineStatus || "Unknown"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Last Connection
                    </p>
                    <p className="text-sm text-gray-700">
                      {formatDate(screen.lastConnection)}
                    </p>
                  </div>
                </div>

                {/* Last Position */}
                {screen.lastPosition && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                      Last Position
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center">
                        <FiMapPin className="w-4 h-4 text-red-500 mr-1" />
                        <span className="text-sm text-gray-700 break-all">
                          {screen.lastPosition.coordinates[0]?.toFixed(6)},{" "}
                          {screen.lastPosition.coordinates[1]?.toFixed(6)}
                        </span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <BsClock className="w-3 h-3 mr-1" />
                        {formatDate(screen.lastPosition.timestamp)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-500">No screen attached</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200 mt-auto">
          <button
            onClick={() => {
              setAddVehicleOpen(true);
              setEditVehiclesData(vehicle);
            }}
            className="flex items-center justify-center h-10 w-10 bg-yellow hover:bg-yellow text-black rounded-lg transition-colors"
          >
            <GoPencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(vehicle._id)}
            className="flex items-center justify-center h-10 w-10 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            <BsTrash3 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  // Show loader while checking owner profile status
  if (ownerProfileExists === null || loading) {
    return (
      <OwnerLayout title="Mobile Locations" ownerDetails={user}>
        <div className="flex justify-center items-center h-64">
          <RotatingLines
            strokeColor="#d97706"
            strokeWidth="5"
            animationDuration="0.75"
            width="96"
            visible={true}
          />
        </div>
      </OwnerLayout>
    );
  }

  // Show create profile message only after loading is complete and owner doesn't exist
  if (!ownerProfileExists) {
    return (
      <OwnerLayout title="Mobile Locations" ownerDetails={user}>
        <div className="px-1 sm:px-3 lg:px-8 mt-10">
          <div className="text-center font-bold text-red-600 text-xl my-4">
            Please create Owner profile first.
          </div>
          <div className="text-center">
            <Link
              to="/ownerprofile"
              className="inline-flex items-center justify-center rounded-md bg-yellow px-4 py-2.5 text-base font-semibold text-gray-700 shadow-sm hover:bg-opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellowColor"
            >
              Create Owner Profile
            </Link>
          </div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <>
      <AddVehicle
        open={addVehicleOpen}
        setOpen={setAddVehicleOpen}
        fetchVehicles={fetchVehicles}
        initialData={initialData}
        vehiclesData={editVehiclesData}
      />
      <OwnerLayout title="Mobile Locations" ownerDetails={user}>
        <div className="px-1 sm:px-3 lg:px-8 bg-gray-50 min-h-screen">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between sticky top-16 bg-gray-50 py-4 z-10 px-2">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl font-bold text-gray-900">
                My Mobile Locations
              </h1>
              <p className="text-gray-600 mt-1">
                Manage your mobile advertising locations
              </p>
            </div>
            <button
              onClick={() => {
                setAddVehicleOpen(true);
                setEditVehiclesData(initialData);
              }}
              className="flex items-center justify-center rounded-lg bg-yellow hover:bg-yellow px-4 py-2.5 text-sm font-semibold text-black shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600"
            >
              <FiPlus className="w-4 h-4 mr-2" />
              Add Mobile Location
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <RotatingLines
                strokeColor="#d97706"
                strokeWidth="5"
                animationDuration="0.75"
                width="96"
                visible={true}
              />
            </div>
          ) : (
            <div className="mt-5">
              {vehiclesData.length === 0 ? (
                <div className="text-center py-12">
                  <BiCar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No mobile locations found
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Get started by adding your first mobile location
                  </p>
                  <button
                    onClick={() => {
                      setAddVehicleOpen(true);
                      setEditVehiclesData(initialData);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-yellow hover:bg-yellow text-black rounded-lg transition-colors"
                  >
                    <FiPlus className="w-4 h-4 mr-2" />
                    Add Mobile Location
                  </button>
                </div>
              ) : (
                <>
                  {/* Stats Bar */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center">
                        <BiCar className="w-8 h-8 text-black" />
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">
                            Total Locations
                          </p>
                          <p className="text-2xl font-bold text-gray-900">
                            {vehiclesData.length}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center">
                        <BsShield className="w-8 h-8 text-green-600" />
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">Approved</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {
                              vehiclesData.filter(
                                (v) => v.status === "Approved"
                              ).length
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center">
                        <MdOutlineScreenshotMonitor className="w-8 h-8 text-purple-600" />
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">
                            Active Screens
                          </p>
                          <p className="text-2xl font-bold text-gray-900">
                            {vehiclesData.reduce(
                              (total, vehicle) =>
                                total +
                                (vehicle.screens?.filter(
                                  (s) => s.status === "Active"
                                ).length || 0),
                              0
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-center">
                        <FiMapPin className="w-8 h-8 text-orange-600" />
                        <div className="ml-3">
                          <p className="text-sm text-gray-500">Locations</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {new Set(vehiclesData.map((v) => v.location)).size}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Vehicles Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vehiclesData.map((vehicle, index) => (
                      <VehicleCard
                        key={vehicle._id || index}
                        vehicle={vehicle}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </OwnerLayout>
    </>
  );
}

export default Vehicles;
